// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/** What the container told us before anything was decoded. */
export interface VideoFacts {
  readonly name: string;
  readonly bytes: number;
  /** Seconds. Some containers under-report; treated as advisory. */
  readonly duration: number;
  readonly width: number;
  readonly height: number;
  readonly codec: string;
  /** Whether this browser's VideoDecoder will actually take that codec. */
  readonly decodable: boolean;
  /** Timestamps in seconds of every key packet, found without decoding. */
  readonly keyframes: readonly number[];
}

/** One sampled frame's place in the index. */
export interface FrameRef {
  readonly index: number;
  /** Seconds into the video. */
  readonly time: number;
}

export interface ScanProgress {
  readonly done: number;
  readonly total: number;
  /** Frames per second of wall-clock work, measured, not assumed. */
  readonly rate: number;
  /** Seconds remaining at the current rate, or null before there is enough data. */
  readonly eta: number | null;
}

/** A contiguous run of high-scoring frames, after smoothing and suppression. */
export interface Moment {
  readonly start: number;
  readonly end: number;
  /** The single best-scoring frame inside the moment. */
  readonly peakTime: number;
  readonly peakScore: number;
  readonly peakIndex: number;
}

export interface MomentSettings {
  /** Frames in the temporal median window. Odd; 1 disables smoothing. */
  readonly medianWindow: number;
  /** Moments closer together than this many seconds are merged. */
  readonly mergeGap: number;
  /** Moments shorter than this many seconds are padded out to it. */
  readonly minDuration: number;
  /** How many moments to show. Rank, never threshold. */
  readonly topN: number;
}

export const DEFAULT_SETTINGS: MomentSettings = {
  medianWindow: 3,
  mergeGap: 2,
  minDuration: 2,
  topN: 12,
};

export type LogLevel = 'info' | 'good' | 'warn' | 'error';
